import React, { useEffect, useState } from "react";
import { getInvoicePages } from "@/lib/apiClient";
import { FileText, Mail, Image as ImageIcon, AlignLeft, ZoomIn, ZoomOut, Scissors } from "lucide-react";
import { cn } from "@/lib/utils";
import { SplitMergeDialog } from "@/components/SplitMergeDialog";

export const DocumentViewer = ({ inv, user }) => {
  const [pages, setPages] = useState(null);
  const [mode, setMode] = useState("page");
  const [zoom, setZoom] = useState(1);
  const [tools, setTools] = useState(false);
  const decided = inv.status === "approved" || inv.status === "rejected";

  useEffect(() => {
    setPages(null);
    if (inv.page_count > 0 || inv.source === "image") getInvoicePages(inv.id).then((r) => setPages(r.pages || [])).catch(() => setPages([]));
    else setPages([]);
  }, [inv.id, inv.page_count, inv.source]);

  const hasPages = pages && pages.length > 0;
  const showPage = mode === "page" && hasPages;

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden flex flex-col min-w-0" data-testid="document-viewer">
      <SplitMergeDialog open={tools} onOpenChange={setTools} inv={inv} pages={pages || []} user={user} />
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2.5 text-sm font-medium">
        <span className="flex items-center gap-2 truncate min-w-0">
          {inv.source === "pdf" ? <FileText className="h-4 w-4" /> : inv.source === "image" ? <ImageIcon className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
          <span className="truncate">{inv.filename || "Email source"}</span>
          {hasPages && <span className="text-[11px] text-muted-foreground">· {pages.length} page{pages.length === 1 ? "" : "s"}</span>}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {inv.source === "pdf" && !decided && (
            <button data-testid="split-merge-open" onClick={() => setTools(true)} title="Split pages into separate invoices or merge with another" className="mr-1 flex items-center gap-1 rounded border border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:text-foreground"><Scissors className="h-3 w-3" /> Split / merge</button>
          )}
          {showPage && (
            <>
              <button data-testid="doc-zoom-out" onClick={() => setZoom((z) => Math.max(0.6, z - 0.2))} className="rounded p-1 text-muted-foreground hover:text-foreground"><ZoomOut className="h-3.5 w-3.5" /></button>
              <button data-testid="doc-zoom-in" onClick={() => setZoom((z) => Math.min(2.4, z + 0.2))} className="rounded p-1 text-muted-foreground hover:text-foreground"><ZoomIn className="h-3.5 w-3.5" /></button>
            </>
          )}
          {hasPages && (
            <div className="ml-1 flex rounded-md border border-border p-0.5 text-[11px]">
              <button data-testid="doc-mode-page" onClick={() => setMode("page")} className={cn("rounded px-2 py-0.5", mode === "page" ? "bg-muted text-foreground" : "text-muted-foreground")}>Page</button>
              <button data-testid="doc-mode-text" onClick={() => setMode("text")} className={cn("rounded px-2 py-0.5", mode === "text" ? "bg-muted text-foreground" : "text-muted-foreground")}>Text</button>
            </div>
          )}
        </div>
      </div>
      {showPage ? (
        <div className="d20-scroll flex-1 overflow-auto bg-neutral-900/60 p-3 max-h-[640px]" data-testid="document-pages">
          <div className="mx-auto space-y-3" style={{ width: `${zoom * 100}%`, maxWidth: zoom <= 1 ? "100%" : undefined }}>
            {pages.map((src, i) => (
              <img key={`page-${i + 1}`} src={src} alt={`Page ${i + 1}`} data-testid={`document-page-${i + 1}`} className="w-full rounded shadow-lg bg-white" />
            ))}
          </div>
        </div>
      ) : (
        <pre data-testid="document-source" className="d20-scroll flex-1 overflow-auto whitespace-pre-wrap p-4 text-xs font-mono text-muted-foreground max-h-[640px]">
          {inv.raw_text || "No source text captured."}
        </pre>
      )}
      {pages === null && <div className="border-t border-border px-4 py-2 text-[11px] text-muted-foreground">Loading page preview…</div>}
      {mode === "page" && hasPages && <div className="flex items-center gap-1.5 border-t border-border px-4 py-2 text-[11px] text-muted-foreground"><AlignLeft className="h-3 w-3" /> Compare the page with the extracted fields on the right. Switch to Text to see what the machine read.</div>}
    </div>
  );
};
